import React, { useState, useEffect } from 'react';
import { onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { Truck, Plus, Pencil, Trash2, X } from 'lucide-react';
import { vehiclesCol } from './database.js';
import { createDoc, updateOne, deleteOne } from './lib/db-crud.js';

// "2018 Ford F-150" with optional trim
export function vehicleLabel(v) {
  if (!v) return '';
  const trim = v.trim ? ` ${v.trim}` : '';
  return `${v.year || ''} ${v.make || ''} ${v.model || ''}${trim}`.trim();
}

// Subscribe to vehicles for one customer. Used by Jobs.jsx too.
export function useCustomerVehicles(customerId) {
  const [vehicles, setVehicles] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!customerId) return;
    const q = query(
      vehiclesCol(),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setVehicles(snap.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) })));
        setLoaded(true);
      },
      (err) => {
        console.error('Vehicles load failed:', err);
        setLoaded(true);
      }
    );
    return unsub;
  }, [customerId]);

  return { vehicles, loaded };
}

export default function Vehicles({ customerId, autoOpenAdd, onAutoOpenConsumed }) {
  const { vehicles, loaded } = useCustomerVehicles(customerId);
  const [modalState, setModalState] = useState(null);

  function blank() {
    return {
      year: '', make: '', model: '', trim: '',
      vin: '', color: '', plate: '', odometer: '',
      customFields: [],
    };
  }
  function openAdd() { setModalState(blank()); }

  // Auto-open the Add Vehicle modal when arriving from Home's
  // + NEW → Vehicle picker (which navigates here with ?addVehicle=1).
  // Only fires once per nav — onAutoOpenConsumed lets Jobs.jsx clear
  // the URL param so a refresh doesn't re-trigger the modal.
  useEffect(() => {
    if (!autoOpenAdd) return;
    setModalState(blank());
    onAutoOpenConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAdd]);
  function openEdit(v) {
    setModalState({
      id: v.id,
      year: v.year ?? '',
      make: v.make ?? '',
      model: v.model ?? '',
      trim: v.trim ?? '',
      vin: v.vin ?? '',
      color: v.color ?? '',
      plate: v.plate ?? '',
      odometer: v.odometer ?? '',
      customFields: Array.isArray(v.customFields) ? v.customFields : [],
    });
  }

  async function handleDelete(v) {
    if (!confirm(`Delete ${vehicleLabel(v)}?\n\nJobs tied to this vehicle will keep their history but show "(deleted vehicle)".`)) return;
    try {
      await deleteOne(vehiclesCol(), v.id);
    } catch (e) {
      alert(`Could not delete: ${e.message}`);
    }
  }

  return (
    <section className="vehicles-section">
      <div className="section-header">
        <h2>Vehicles</h2>
        <button className="btn-secondary" onClick={openAdd}>
          <Plus size={14} strokeWidth={2.5} />
          Add Vehicle
        </button>
      </div>

      {!loaded && <p className="muted small">Loading vehicles…</p>}

      {loaded && vehicles.length === 0 && (
        <p className="muted small empty-inline">No vehicles yet. Add one before creating a job.</p>
      )}

      {loaded && vehicles.length > 0 && (
        <div className="vehicle-list">
          {vehicles.map((v) => (
            <VehicleRow key={v.id} vehicle={v} onEdit={() => openEdit(v)} onDelete={() => handleDelete(v)} />
          ))}
        </div>
      )}

      {modalState && (
        <VehicleModal
          initial={modalState}
          customerId={customerId}
          onClose={() => setModalState(null)}
        />
      )}
    </section>
  );
}

function VehicleRow({ vehicle, onEdit, onDelete }) {
  const detail = [
    vehicle.color,
    vehicle.plate && `plate ${vehicle.plate}`,
    vehicle.odometer && `${Number(vehicle.odometer).toLocaleString()} mi`,
  ].filter(Boolean).join(' · ');

  // Custom fields the mechanic added themselves (Engine, Trans, whatever).
  const customFields = (vehicle.customFields || [])
    .filter((cf) => cf && cf.label && cf.value);

  return (
    <div className="vehicle-row">
      <Truck size={20} color="var(--text-2)" strokeWidth={2} style={{ flexShrink: 0 }} />
      <div className="vehicle-row-body">
        <div className="vehicle-name">{vehicleLabel(vehicle)}</div>
        {detail && <div className="muted small">{detail}</div>}
        {vehicle.vin && <div className="muted small mono">VIN {vehicle.vin}</div>}
        {customFields.length > 0 && (
          <div className="custom-field-list">
            {customFields.map((cf, i) => (
              <span key={i} className="custom-field-chip">
                <span className="custom-field-label">{cf.label}:</span>
                <span className="custom-field-value">{cf.value}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="vehicle-row-actions">
        <button className="btn-link" onClick={onEdit}>
          <Pencil size={13} /> Edit
        </button>
        <button className="btn-link danger" onClick={onDelete}>
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  );
}

function VehicleModal({ initial, customerId, onClose }) {
  const isEditing = !!initial.id;
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  // ── Custom fields management ──
  function addCustomField() {
    setForm((f) => ({
      ...f,
      customFields: [...(f.customFields || []), { id: `cf_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, label: '', value: '' }],
    }));
  }
  function updateCustomField(id, field, val) {
    setForm((f) => ({
      ...f,
      customFields: (f.customFields || []).map((cf) =>
        cf.id === id ? { ...cf, [field]: val } : cf
      ),
    }));
  }
  function removeCustomField(id) {
    setForm((f) => ({
      ...f,
      customFields: (f.customFields || []).filter((cf) => cf.id !== id),
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const year = Number(form.year);
    if (!year || year < 1900 || year > new Date().getFullYear() + 2) {
      setError('Enter a valid year.');
      return;
    }
    if (!form.make.trim() || !form.model.trim()) {
      setError('Year, make, and model are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Drop empty custom fields (label OR value missing). Keep the id so
      // edits remain stable across renders.
      const cleanCustomFields = (form.customFields || [])
        .map((cf) => ({ id: cf.id, label: (cf.label || '').trim(), value: (cf.value || '').trim() }))
        .filter((cf) => cf.label && cf.value);

      const payload = {
        customerId,
        year,
        make: form.make.trim(),
        model: form.model.trim(),
        trim: form.trim.trim() || null,
        vin: form.vin.trim().toUpperCase() || null,
        color: form.color.trim() || null,
        plate: form.plate.trim().toUpperCase() || null,
        odometer: form.odometer ? Number(form.odometer) : null,
        customFields: cleanCustomFields,
      };
      if (isEditing) {
        await updateOne(vehiclesCol(), initial.id, payload);
      } else {
        await createDoc(vehiclesCol(), payload);
      }
      onClose();
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Vehicle' : 'Add Vehicle'}</h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row-pair">
            <label className="form-row" style={{ flex: '0 0 100px' }}>
              <span>Year *</span>
              <input
                type="number" required min={1900} max={new Date().getFullYear() + 2}
                inputMode="numeric"
                value={form.year} onChange={(e) => update('year', e.target.value)}
                placeholder="2018" autoFocus
              />
            </label>
            <label className="form-row" style={{ flex: 1 }}>
              <span>Make *</span>
              <input
                type="text" required maxLength={40}
                value={form.make} onChange={(e) => update('make', e.target.value)}
                placeholder="Ford"
              />
            </label>
          </div>
          <div className="form-row-pair">
            <label className="form-row" style={{ flex: 1 }}>
              <span>Model *</span>
              <input
                type="text" required maxLength={40}
                value={form.model} onChange={(e) => update('model', e.target.value)}
                placeholder="F-150"
              />
            </label>
            <label className="form-row" style={{ flex: 1 }}>
              <span>Trim</span>
              <input
                type="text" maxLength={40}
                value={form.trim} onChange={(e) => update('trim', e.target.value)}
                placeholder="XLT"
              />
            </label>
          </div>
          <div className="form-row-pair">
            <label className="form-row" style={{ flex: 1 }}>
              <span>Color</span>
              <input
                type="text" maxLength={20}
                value={form.color} onChange={(e) => update('color', e.target.value)}
                placeholder="Silver"
              />
            </label>
            <label className="form-row" style={{ flex: 1 }}>
              <span>Plate</span>
              <input
                type="text" maxLength={12}
                value={form.plate} onChange={(e) => update('plate', e.target.value)}
                placeholder="ABC-1234"
              />
            </label>
          </div>
          <label className="form-row">
            <span>VIN</span>
            <input
              type="text" maxLength={17}
              value={form.vin} onChange={(e) => update('vin', e.target.value)}
              placeholder="1FTFW1ET5DFC10312"
              style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}
            />
          </label>
          <label className="form-row">
            <span>Odometer (mi)</span>
            <input
              type="number" min={0} inputMode="numeric"
              value={form.odometer} onChange={(e) => update('odometer', e.target.value)}
              placeholder="85000"
            />
          </label>

          {/* Custom fields — mechanic adds whatever they want (Engine, Trans, Lift kit, etc.) */}
          <div className="custom-field-form">
            {(form.customFields || []).length > 0 && (
              <div className="form-section-header">Additional details</div>
            )}
            {(form.customFields || []).map((cf) => (
              <div key={cf.id} className="custom-field-input-row">
                <input
                  type="text" maxLength={40}
                  className="cf-label-input"
                  placeholder="Label (e.g., Engine)"
                  value={cf.label}
                  onChange={(e) => updateCustomField(cf.id, 'label', e.target.value)}
                />
                <input
                  type="text" maxLength={120}
                  className="cf-value-input"
                  placeholder="Value (e.g., 5.7L Hemi V8)"
                  value={cf.value}
                  onChange={(e) => updateCustomField(cf.id, 'value', e.target.value)}
                />
                <button
                  type="button"
                  className="cf-remove-btn"
                  onClick={() => removeCustomField(cf.id)}
                  aria-label="Remove this field"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            <button type="button" className="btn-secondary cf-add-btn" onClick={addCustomField}>
              <Plus size={14} strokeWidth={2.5} />
              Add additional
            </button>
          </div>

          {error && <p className="form-error">{error}</p>}
          <div className="form-actions">
            <button type="button" className="btn-link" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" disabled={submitting}>
              {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
